/* eslint-disable no-unused-vars */
import {
  IClientDetails,
  ISecurityIdentifierDetail,
  ITradeAllocation,
  TradeMessageSubType,
  TradeMessageType,
  TradeOpenClose,
} from "./ENUMBondTradeMessage";
import { ISwapLegDetails } from "./ENUMSwapTradeMessage";

// LQC-2676 - the POST /Trade/BondOption message. Read off
// LQ.Application/Trades/BondOption/Incoming*.cs rather than inferred, the same rule the
// Future ticket learned the hard way. Swaption (LQC-2677) shares the vocabulary below,
// which is why this file is named for options rather than for bond options.
//
// The envelope is NOT the flat ITradeMessage that Bond and Future share: an option adds
// securityDetails (the option terms) and underlyingSecurity (what it is written on).

// Payer / Receiver also exist in the API enum but ValidateBondOptionOptionType rejects
// them - they are the Swaption pair.
export enum TradeOptionType {
  Put = "Put",
  Call = "Call",
  Payer = "Payer",
  Receiver = "Receiver",
}

export enum TradeOptionStyle {
  American = "American",
  European = "European",
  Bermudan = "Bermudan",
}

export enum TradePremiumType {
  Spot = "Spot",
  Forward = "Forward",
}

export enum TradeDelivery {
  Physical = "Physical",
  Cash = "Cash",
}

// Spec F7 / F10 dropdowns, shared by Bond Option and Swaption. Bermudan needs exercise
// dates we cannot capture yet, so it is not offered.
export const OPTION_EXERCISE_TYPES: TradeOptionStyle[] = [
  TradeOptionStyle.European,
  TradeOptionStyle.American,
];
export const OPTION_PREMIUM_TYPES: TradePremiumType[] = [
  TradePremiumType.Spot,
  TradePremiumType.Forward,
];
export const OPTION_DELIVERIES: TradeDelivery[] = [TradeDelivery.Physical, TradeDelivery.Cash];

export enum TradeStrikeQuotation {
  Price = "Price",
  Yield = "Yield",
}

// What a bond option ticket can send. The validator accepts Put and Call only.
export const BOND_OPTION_TYPES: TradeOptionType[] = [
  TradeOptionType.Put,
  TradeOptionType.Call,
];

// LQC-2959 - screen-only: split into Buy/Sell and Payer/Receiver before sending.
export enum SwaptionDirection {
  BuyPayer = "Buy Payer",
  SellPayer = "Sell Payer",
  BuyReceiver = "Buy Receiver",
  SellReceiver = "Sell Receiver",
}

export const SWAPTION_DIRECTIONS: SwaptionDirection[] = [
  SwaptionDirection.BuyPayer,
  SwaptionDirection.SellPayer,
  SwaptionDirection.BuyReceiver,
  SwaptionDirection.SellReceiver,
];

// The option terms. optionStyle, premiumType and delivery are conventions we default
// rather than ask for; strike and strikeQuotation are the only optional fields.
export interface IOptionSecurityDetails {
  optionType: TradeOptionType;
  optionStyle: TradeOptionStyle;
  premiumType: TradePremiumType;
  delivery: TradeDelivery;
  strike?: number;
  strikeQuotation?: TradeStrikeQuotation;
  expiryDate: string; // yyyy-MM-dd
  currencyPayment: string; // exactly 3 characters, validated against the DB
  calendar: string; // FpML business-centre code, validated against the DB
}

// Same fields as ITradeDetails plus openClose, which BondOption requires and Bond and
// Future do not have at all.
export interface IOptionTradeDetails {
  tradeDate: string; // yyyy-MM-dd
  direction: string;
  nominal: number;
  settlementDate: string; // yyyy-MM-dd
  openClose: TradeOpenClose;
  transactionNumber?: string;
}

// The option's OWN security. identifierDetails must stay EMPTY: the option does not
// exist yet and the back end manufactures it. Sending the underlying ISIN here fails -
// ValidateSecurityIdentifierDetails runs whenever the list is non-empty and then demands
// a UTI / ExternalID / ExternalKey.
export interface IOptionSecurity {
  identifierDetails?: ISecurityIdentifierDetail[];
}

export interface IBondOptionTradeMessage {
  messageType: TradeMessageType;
  subType: TradeMessageSubType;
  clientDetails: IClientDetails;
  securityDetails: IOptionSecurityDetails;
  // The bond the option is written on - one ISIN, and the list must be non-empty.
  underlyingSecurity: {
    identifierDetails: ISecurityIdentifierDetail[];
  };
  security: IOptionSecurity;
  tradeDetails: IOptionTradeDetails;
  allocations: ITradeAllocation[];
}

// LQC-2677 - POST /Trade/Swaption. Option terms over a swap underlying, so it composes
// this file's vocabulary with the swap leg shape. Read off
// LQ.Application/Trades/Swaption/Incoming*.cs.
//
// A swaption leg is the swap leg WITHOUT spread - the model has no such property.
export type ISwaptionLegDetails = Omit<ISwapLegDetails, "spread">;

// exerciseType, not optionStyle: the swaption model names the same enum differently.
export interface ISwaptionSecurityDetails {
  optionType: TradeOptionType;
  exerciseType: TradeOptionStyle;
  premiumType: TradePremiumType;
  delivery: TradeDelivery;
  strike?: number;
  strikeQuotation?: TradeStrikeQuotation;
  expiryDate: string; // yyyy-MM-dd, must be >= settlementDate
  // The OPTION's own cleared flag. The underlying swap carries a separate one.
  cleared: boolean;
  currencyPayment: string;
  calendar: string;
}

export interface ISwaptionUnderlyingDetails {
  startDate: string; // yyyy-MM-dd
  maturityDate: string; // yyyy-MM-dd, must be >= settlementDate
  firstCouponFrom?: string;
  cleared: boolean;
  // ZCS underlying only (LQC-2987). Same bounds as the Swap: 0, null or > 1.
  cpi?: number;
  cpiIndex?: string;
  securityDetailsLeg1: ISwaptionLegDetails;
  securityDetailsLeg2: ISwaptionLegDetails;
}

// Direction is Buy / Sell, NOT Pay / Receive: SwaptionModelService calls the shared
// ValidateDirection, which accepts only those two. The LQC-2677 description says
// Pay / Receive and is wrong, exactly as LQC-2494 was about BuyFuture.
//
// openClose is not [Required] on the model, but ValidateOpenClose runs unconditionally
// and rejects null AND Substitute, so Open or Close must be sent.
export interface ISwaptionTradeDetails {
  tradeDate: string; // yyyy-MM-dd
  direction: string; // Buy | Sell
  nominal: number;
  settlementDate: string; // yyyy-MM-dd
  openClose: TradeOpenClose;
  clearingHouse?: string;
  transactionNumber?: string;
}

export interface ISwaptionTradeMessage {
  messageType: TradeMessageType;
  subType: TradeMessageSubType;
  clientDetails: IClientDetails;
  // Empty, same trap as a bond option: a non-empty list makes the API demand a UTI.
  security: { identifierDetails?: ISecurityIdentifierDetail[] };
  securityDetails: ISwaptionSecurityDetails;
  underlyingSecurityDetails: ISwaptionUnderlyingDetails;
  tradeDetails: ISwaptionTradeDetails;
  allocations: ITradeAllocation[];
}
